import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Checkbox,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import { getContrastColor } from "../utils/fontColor";
import * as prodService from "./../services/saleng";

export default function ProductSelectDialog({
  open,
  onClose,
  onConfirm,
  products,
  categories,
  initialSelectedId,
  initialSelectedIds,
  productsStock,
  multi = false,
}) {
  const sortedCategories = useMemo(() => {
    const arr = Array.isArray(categories) ? [...categories] : [];
    return arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [categories]);

  const [layout, setLayout] = useState([]);
  const [catFilter, setCatFilter] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(initialSelectedId || null);
  const [selectedIds, setSelectedIds] = useState(
    Array.isArray(initialSelectedIds) ? new Set(initialSelectedIds) : new Set(),
  );

  const mergedProducts = (products || []).map((p) => {
    const stockData = (productsStock || []).find(
      (s) => s.productId === p.productId,
    );
    return {
      ...p,
      stock: stockData ? stockData.stock : 0,
    };
  });

  useEffect(() => {
    if (open) {
      setSearch("");
      setSelectedId(initialSelectedId || null);
      setSelectedIds(
        Array.isArray(initialSelectedIds)
          ? new Set(initialSelectedIds)
          : new Set(),
      );
      const firstCatName = sortedCategories[0]?.name ?? null;
      setCatFilter((prev) => prev ?? firstCatName);

      // Fetch layout
      prodService
        .getUserLayout(1)
        .then((res) => {
          setLayout(res?.data || []);
        })
        .catch((err) => console.error("โหลด Layout ล้มเหลว:", err));
    }
  }, [open, initialSelectedId, initialSelectedIds, sortedCategories]);

  const sortedProducts = useMemo(() => {
    if (!layout.length) return mergedProducts;

    const box1Layout = layout.filter((l) => l.box === "box1");
    // Create a map for quick lookup of order
    const layoutOrder = new Map(box1Layout.map((l, i) => [l.productId, i]));

    return [...mergedProducts].sort((a, b) => {
      const orderA = layoutOrder.has(a.productId)
        ? layoutOrder.get(a.productId)
        : 9999;
      const orderB = layoutOrder.has(b.productId)
        ? layoutOrder.get(b.productId)
        : 9999;
      return orderA - orderB;
    });
  }, [mergedProducts, layout]);

  const filtered = useMemo(() => {
    const searchText = search.trim().toLowerCase();
    return (sortedProducts || []).filter((p) => {
      if (catFilter && p.category?.name !== catFilter) return false;
      if (searchText) {
        const title = String(p.title ?? "").toLowerCase();
        const code = String(p.code ?? "").toLowerCase();
        if (!title.includes(searchText) && !code.includes(searchText)) return false;
      }
      return true;
    });
  }, [sortedProducts, catFilter, search]);

  const isSelected = (id) => (multi ? selectedIds.has(id) : selectedId === id);

  const toggleSelect = (id) => {
    if (multi) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    } else {
      setSelectedId((prev) => (prev === id ? null : id));
    }
  };

  const clearSelection = () => {
    if (multi) setSelectedIds(new Set());
    else setSelectedId(null);
  };

  const confirm = () => {
    if (multi) {
      const selected = (mergedProducts || []).filter((p) => selectedIds.has(p.id));
      onConfirm(selected);
    } else {
      const prod = (mergedProducts || []).find((p) => p.id === selectedId);
      if (prod) onConfirm(prod);
    }
  };

  const selectedCount = multi ? selectedIds.size : selectedId ? 1 : 0;

  // เลือกทั้งประเภท
  const selectWholeCategory = () => {
    if (!multi || !catFilter) return;
    const idsInCat = (mergedProducts || [])
      .filter((p) => p.category?.name === catFilter)
      .map((p) => p.id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      idsInCat.forEach((id) => next.add(id));
      return next;
    });
  };

  // ล้างทั้งประเภท
  const clearWholeCategory = () => {
    if (!multi || !catFilter) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const p of mergedProducts || []) {
        if (p.category?.name === catFilter) next.delete(p.id);
      }
      return next;
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          height: "85vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden"
        }
      }}
    >
      {/* Header */}
      <DialogTitle className="bg-white border-b border-gray-100 p-4 sm:p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">
            เลือกสินค้า
            {multi && <span className="text-sm font-normal text-gray-500 ml-2">(เลือกได้หลายรายการ)</span>}
          </h2>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <div className="relative group w-full sm:w-64">
            <SearchIcon className="absolute left-3 top-2.5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            <input
              type="text"
              placeholder="ค้นหาสินค้า..."
              className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-transparent hover:bg-white hover:border-gray-200 focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl text-sm outline-none transition-all"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <button onClick={onClose} className="sm:hidden self-end p-2 -mt-12 text-gray-400">
            <CloseIcon />
          </button>
        </div>
      </DialogTitle>

      <DialogContent className="bg-slate-50/50 p-0 overflow-hidden flex flex-col sm:flex-row h-full">
        {/* Sidebar Categories */}
        <div className="w-full sm:w-60 bg-white border-b sm:border-b-0 sm:border-r border-gray-200 overflow-x-auto sm:overflow-y-auto flex sm:flex-col shrink-0 custom-scrollbar">
          {sortedCategories.map((cat) => {
            const isActive = catFilter === cat.name;
            return (
              <button
                key={cat.id}
                onClick={() => setCatFilter(cat.name)}
                className={`
                    flex-shrink-0 px-4 py-3 sm:px-5 sm:py-4 text-left font-medium text-sm transition-all border-b-2 sm:border-b-0 sm:border-l-4
                    ${isActive
                    ? "text-blue-600 bg-blue-50/50 border-blue-500"
                    : "text-gray-600 hover:bg-gray-50 border-transparent hover:border-gray-200"}
                 `}
              >
                {cat.name}
              </button>
            );
          })}
        </div>

        {/* Product Grid */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 custom-scrollbar">
          {filtered.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-gray-400 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50 min-h-[300px]">
              <SearchIcon sx={{ fontSize: 48, opacity: 0.2 }} />
              <p className="mt-2 text-sm">ไม่พบสินค้าในเงื่อนไขที่ค้นหา</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
              {filtered.map((p) => {
                const sel = isSelected(p.id);
                const bg = p?.subCategory?.color || "#e0e0e0";
                const textColor = getContrastColor(bg);

                return (
                  <div
                    key={p.id}
                    onClick={() => toggleSelect(p.id)}
                    className={`
                              relative h-24 rounded-2xl cursor-pointer transition-all duration-200 group
                              flex flex-col items-center justify-center p-2 text-center shadow-sm select-none
                              ${sel
                        ? "ring-4 ring-offset-2 ring-blue-500 scale-[0.98] z-10"
                        : "hover:scale-[1.03] hover:shadow-md ring-1 ring-black/5"}
                           `}
                    style={{ backgroundColor: bg }}
                  >
                    {multi && sel && (
                      <div className="absolute top-1 right-1 text-blue-600 bg-white rounded-full shadow-sm">
                        <CheckCircleIcon fontSize="small" />
                      </div>
                    )}

                    <h3
                      className="font-bold text-base sm:text-lg line-clamp-2 px-1 break-words w-full leading-tight"
                      style={{ color: textColor }}
                    >
                      {p.title}
                    </h3>
                    <p
                      className="text-[11px] sm:text-xs mt-1 px-1 break-words w-full leading-tight"
                      style={{ color: textColor, opacity: 0.9 }}
                    >
                      {p.code ? `รหัสสินค้า: ${p.code}` : "\u00a0"}
                    </p>
                    {p.stock !== undefined && p.stock !== 0 && (
                      <span className="text-[10px] mt-1 px-1.5 py-0.5 rounded-full bg-black/10 text-black/70 font-bold backdrop-blur-sm">
                        Stock: {p.stock}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>

      {/* Footer Actions */}
      <DialogActions className="bg-white border-t border-gray-100 p-4 sm:px-6 flex flex-col sm:flex-row gap-3">
        <div className="flex items-center gap-2 mr-auto mb-2 sm:mb-0 w-full sm:w-auto overflow-x-auto pb-1 sm:pb-0">
          {multi ? (
            <>
              <span className="px-3 py-1 rounded-lg bg-blue-100 text-blue-700 text-xs font-bold whitespace-nowrap">
                เลือก {selectedCount}
              </span>
              <div className="h-4 w-px bg-gray-200 mx-1"></div>
              <button onClick={selectWholeCategory} disabled={!catFilter} className="text-xs text-blue-600 hover:bg-blue-50 px-2 py-1 rounded transition-colors whitespace-nowrap disabled:opacity-50">
                เลือกทั้งกลุ่ม
              </button>
              <button onClick={clearWholeCategory} disabled={!catFilter} className="text-xs text-gray-500 hover:bg-gray-100 px-2 py-1 rounded transition-colors whitespace-nowrap disabled:opacity-50">
                ล้างกลุ่มนี้
              </button>
              <button onClick={clearSelection} className="text-xs text-red-600 hover:bg-red-50 px-2 py-1 rounded transition-colors whitespace-nowrap">
                ล้างทั้งหมด
              </button>
            </>
          ) : (
            selectedId && (
              <button onClick={clearSelection} className="text-xs text-red-600 hover:bg-red-50 px-2 py-1 rounded transition-colors whitespace-nowrap">
                ยกเลิกการเลือก
              </button>
            )
          )}
        </div>

        <div className="flex gap-3 w-full sm:w-auto">
          <button
            onClick={onClose}
            className="flex-1 sm:flex-none px-6 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-50 transition-all text-sm"
          >
            ยกเลิก
          </button>
          <button
            onClick={confirm}
            disabled={selectedCount === 0}
            className="flex-1 sm:flex-none px-8 py-2.5 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 shadow-md shadow-blue-200 transition-all active:scale-95 disabled:opacity-50 disabled:shadow-none text-sm"
          >
            ยืนยัน ({selectedCount})
          </button>
        </div>
      </DialogActions>
    </Dialog>
  );
}
